'use client';

import {
  Box,
  Carousel,
  CarouselItem,
  Flex,
  freddyTheme,
  SelectableBoxGroup,
  SelectableCheckBox
} from '@coolblue-development/freddy';
import addItemToBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/addItemToBundleTrigger';
import removeItemFromBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/removeItemFromBundleTrigger';
import { Plus } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import { useBreakpoints } from '@hooks/useBreakpoints';

import { useConfigurator } from '../ConfiguratorContext';
import { ConfiguratorProduct } from '../types';

export default function Header() {
  const { trackEvent } = useAnalytics();
  const { mainProduct } = useConfigurator();
  const { isMobile } = useBreakpoints();
  const carouselTranslations = useFreddyTranslations('carousel');

  const {
    configurator,
    states: { selectedProductSlot, selectedUseCaseIndex, checkboxValues, configuratorValues },
    actions: { updateConfiguratorValue, setSelectedProductSlot, getProductByUseCaseAndSlot }
  } = useConfigurator();

  const trackChanges = (index: number, isChecked: boolean) => {
    const product = getProductByUseCaseAndSlot({
      useCaseIndex: selectedUseCaseIndex,
      slotIndex: index
    });

    const params = {
      sub_feature: 'configurator_overlay' as const,
      index: index,
      total_indices: configurator.useCases[selectedUseCaseIndex].slots.length,
      item_id: product?.id || 0
    };

    if (isChecked) {
      trackEvent(removeItemFromBundleTrigger(params));
      return;
    }
    trackEvent(addItemToBundleTrigger(params));
  };

  const handleChange = (value: string) => {
    const isChecked = checkboxValues.includes(value);

    trackChanges(Number(value), isChecked);

    const updatedCheckboxValues = isChecked
      ? checkboxValues.filter(item => item !== value)
      : [...checkboxValues, value];

    const updatedConfiguratorValues = (configuratorValues.get(selectedUseCaseIndex) || []).map(
      (item, index) => ({
        ...item,
        selected: updatedCheckboxValues.includes(String(index))
      })
    );

    updateConfiguratorValue(updatedConfiguratorValues);
  };

  const handleGroupChange = (updatedCheckboxValues: string[]) => {
    const addedValue = updatedCheckboxValues.find(v => !checkboxValues.includes(v));
    const removedValue = checkboxValues.find(v => !updatedCheckboxValues.includes(v));
    const toggledValue = addedValue ?? removedValue;

    if (toggledValue !== undefined) {
      handleChange(toggledValue);
    }
  };

  const Card = ({
    index,
    defaultProduct
  }: {
    index: number;
    defaultProduct: ConfiguratorProduct;
  }) => {
    const isActive = index === selectedProductSlot;

    return (
      <SelectableCheckBox
        ariaLabel={defaultProduct?.name}
        value={String(index)}
        sx={{
          '&&': {
            backgroundColor: isActive ? freddyTheme.color.background.interactiveSelected : 'unset',
            boxShadow: `inset 0 0 0 0.1rem ${
              isActive
                ? freddyTheme.color.border.interactiveSelected
                : freddyTheme.color.background.interactiveControlContrastBase
            }`,
            width: '100%',
            padding: freddyTheme.spacing[200]
          },

          '& > div': {
            marginLeft: freddyTheme.spacing[0]
          }
        }}
      >
        <Box
          aria-label={defaultProduct.productType?.singularName}
          component='button'
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          px={0}
          onClick={event => {
            event.preventDefault();
            setSelectedProductSlot(index);
          }}
        >
          <SquareProductImage
            alt={defaultProduct.name || ''}
            imageId={defaultProduct.primaryImage?.id || ''}
            background={'transparent'}
          />
        </Box>
      </SelectableCheckBox>
    );
  };

  if (isMobile) {
    return (
      <Carousel
        fullWidthUntil='md'
        translations={carouselTranslations}
        itemsInView='flexible'
        hideNavigation
        m={0}
      >
        <CarouselItem key={`mainProduct`}>
          <Flex gap={500} alignItems='center'>
            <Box p={200}>
              <SquareProductImage
                alt={mainProduct.name || ''}
                imageId={mainProduct.primaryImage?.id || ''}
                loading='lazy'
              />
            </Box>

            <Plus color={freddyTheme.color.icon.subtle} size='large' />
          </Flex>
        </CarouselItem>
        <SelectableBoxGroup
          legend='configuratorOverlaySlots'
          showLegend={false}
          controlPlacement='start-floating'
          name='configuratorOverlaySlots'
          value={checkboxValues}
          onChange={handleGroupChange}
        >
          {configurator.useCases[selectedUseCaseIndex].slots
            .filter(slot => slot.defaultProduct?.id)
            .map((_, index) => {
              const product = getProductByUseCaseAndSlot({
                useCaseIndex: selectedUseCaseIndex,
                slotIndex: index
              });

              if (!product) {
                return null;
              }

              return (
                <CarouselItem key={`configurator-overlay-slot-${index}`}>
                  <Card index={index} defaultProduct={product} />
                </CarouselItem>
              );
            })}
        </SelectableBoxGroup>
      </Carousel>
    );
  }
  return (
    <Flex gap={500} alignItems='center' mt={400} mb={600}>
      <SquareProductImage
        alt={mainProduct.name || ''}
        imageId={mainProduct.primaryImage?.id || ''}
        loading='lazy'
      />
      <Plus color={freddyTheme.color.icon.subtle} />
      <Flex>
        <SelectableBoxGroup
          legend='configuratorOverlaySlots'
          showLegend={false}
          controlPlacement='start-floating'
          name='configuratorOverlaySlots'
          value={checkboxValues}
          onChange={handleGroupChange}
        >
          <Flex gap={400}>
            {configurator.useCases[selectedUseCaseIndex].slots
              .filter(slot => slot.defaultProduct?.id)
              .map((_, index) => {
                const product = getProductByUseCaseAndSlot({
                  useCaseIndex: selectedUseCaseIndex,
                  slotIndex: index
                });

                if (!product) {
                  return null;
                }

                return (
                  <Card
                    key={`configurator-overlay-slot-${index}`}
                    index={index}
                    defaultProduct={product}
                  />
                );
              })}
          </Flex>
        </SelectableBoxGroup>
      </Flex>
    </Flex>
  );
}
